import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  forwardRef
} from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import {
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { MatOptionModule } from "@angular/material/core";
import { Subscription } from "rxjs";
import { UntilDestroy } from "@ngneat/until-destroy";
import {
  distinctUntilChanged,
  tap,
  debounceTime
} from "rxjs/operators";
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger
} from "@angular/material/autocomplete";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Observable } from "rxjs";
import { PlacesItem } from "@portal-workspace/grow-shared-library";
import { AbstractControlValueAccessor } from "../../abstract-control-value-accessor";

type DropdownItemsType = PlacesItem;
@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'autocomplete-dropdown',
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrls: ['./autocomplete-dropdown.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDropdownComponent),
      multi: true
    }
  ]
})
export class AutocompleteDropdownComponent extends AbstractControlValueAccessor<any> implements OnInit {
  @Input() title?: string;
  @Input() placeholder = "Search...";
  @Input() readOnly = false;
  @Input() required = false;
  @Input() fetchDataFn?: () => Observable<DropdownItemsType[]>; // only fetches data when user types in the input
  @Input() displayWithFn: (item: any) => string = (item) => item?.toString() || '';
  @Input() minSearchLength = 2;

  @Output() itemSelect = new EventEmitter<any>();
  @Output() selectionSubmit = new EventEmitter<any>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger?: MatAutocompleteTrigger;

  subscriptions: Subscription[] = [];
  state: 'loading' | 'selected' | 'error' | 'done' | '' = '';
  filteredItems: any[] = [];
  allItems: any[] = [];

  formBuilder = inject(FormBuilder);
  formControl: FormControl<any | string | null> = this.formBuilder.control(null,
    this.required ? [Validators.required] : []
  );

  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.fetchDataFn) {
      this.subscriptions.push(
        this.formControl.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(value => this.handleDynamicSearch(value))
        ).subscribe()
      );
    }
  }

  private handleDynamicSearch(value: string | any | null): void {
    if (typeof value === 'string' && value.trim().length >= this.minSearchLength) {
      this.state = 'loading';
      this.subscriptions.push(
        this.fetchDataFn!().pipe(
          tap(items => {
            this.filteredItems = items;
            this.state = 'done';
          }),
          tap({
            error: (error) => {
              this.state = 'error';
              this.filteredItems = [];
              console.error('Error fetching search results:', error);
            }
          })
        ).subscribe()
      );
    } else if (typeof value === 'string' && value.trim().length < this.minSearchLength) {
      this.filteredItems = [];
      this.state = 'done';
    }
  }

  handleInputChange(value: string | any | null): void {
    if (typeof value === 'string' && value.trim().length > 0) {
      const filterValue = value.toLowerCase();
      this.filteredItems = this.allItems.filter(item =>
        this.displayWithFn(item).toLowerCase().includes(filterValue)
      );
    } else {
      this.filteredItems = this.allItems;
    }
  }

  onOptionSelected(event: any): void {
    const selected = event.option.value;
    if (selected) {
      this.state = 'selected';
      this.formControl.setValue(selected);
      this.formControl.markAsTouched();
      this.itemSelect.emit(selected);
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.formControl.value) {
      this.selectionSubmit.emit(this.formControl.value);
    }
  }

  override doWriteValue(v?: any | null | undefined): void {
    v = v?.trim();
    this.formControl.setValue(v, { emitEvent: false });
  }

  // The base class only records the flag; without this the inner input stays editable when the
  // bound control is disabled (e.g. the custom-address search box outside edit mode).
  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }
}